import { Button, ButtonSize, ButtonVariant, toast, Typography } from "@kyan/ui";

import { ConnectWalletWrapper } from "#/components/ConnectWalletWrapper/ConnectWalletWrapper";
import { InstrumentSummary } from "#/components/InstrumentSummary/InstrumentSummary";
import { useCancelOrders } from "#/services/kyan/hooks/useCancelOrders";
import { type RFQResponseState } from "#/services/kyan/kyan.types";
import { isBuy } from "#/utils/marketActionConverter";
import { confirm } from "#/utils/modals/confirm";

export interface RFQCancelButtonProps {
  response: RFQResponseState;
  size?: ButtonSize;
  label?: string;
  variant?: ButtonVariant;
  className?: string;
}

export const RFQCancelButton: React.FC<RFQCancelButtonProps> = ({
  response,
  size = "sm",
  label = "Cancel",
  variant = "action",
  className,
}) => {
  const { mutate, isPending } = useCancelOrders({
    onSuccess: () => {
      toast.success({ title: "Order cancelled successfully!" });
    },
    onError: (error) => {
      toast.error({
        title: "Error cancelling order",
        description: error.message,
      });
    },
  });

  const handleCancelOrder = () => {
    confirm({
      header: (
        <Typography element="h2" weight="bold">
          Are you sure you want to cancel this order?
        </Typography>
      ),
      content: (
        <div className="border-divider mt-5 grid gap-4 rounded-lg border">
          {response.legs.map((leg, index) => (
            <InstrumentSummary
              key={index}
              limitPrice={leg.price}
              netPrice={leg.netPrice}
              size={leg.size.toString()}
              isBuy={isBuy(leg.direction)}
              instrumentName={leg.instrumentName}
            />
          ))}
        </div>
      ),
      cancelText: "No, keep it",
      confirmText: "Yes, cancel",
      onConfirm: () => {
        mutate({ order_ids: [response.orderId] });
      },
    });
  };

  return (
    <ConnectWalletWrapper size={size} variant={variant} className={className}>
      <Button
        size={size}
        variant={variant}
        className={className}
        isLoading={isPending}
        onClick={handleCancelOrder}
      >
        {label}
      </Button>
    </ConnectWalletWrapper>
  );
};
